// skipcq: JS-W1028, JS-0128
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useBreweriesSearch } from '../../../hooks/useBreweriesSearch';
import { useBreweryAutocompleteSearch } from '../../../hooks/useBreweryAutocompleteSearch';
import useDebouncedValue from '../../../hooks/useDebouncedValue';

function formatBreweryLabel(brewery) {
  if (!brewery) return '';
  const name = brewery.data?.name ?? brewery.name ?? '';
  const city = brewery.data?.city ?? brewery.city ?? '';
  const state = brewery.data?.state ?? brewery.state ?? '';
  const location = [city, state].filter(Boolean).join(', ');
  return location ? `${name} (${location})` : name;
}

function formatBreweryOptionLabel(option) {
  if (!option?.name) {
    return '';
  }
  if (option.city) {
    return `${option.name} (${option.city}, ${option.state})`;
  }
  return option.name;
}

function OverrideSearchField(params) {
  return (
    <TextField {...params} label="Search brewery" placeholder="Type to search…" />
  );
}

function BreweryStateErrorRow({ group }) {
  return (
    <TableRow>
      <TableCell>{group.breweryName}</TableCell>
      <TableCell>—</TableCell>
      <TableCell colSpan={2}>
        <Typography variant="body2" color="error">
          Fix missing state in CSV before matching breweries.
        </Typography>
      </TableCell>
    </TableRow>
  );
}

BreweryStateErrorRow.propTypes = {
  group: PropTypes.shape({
    breweryName: PropTypes.string.isRequired,
  }).isRequired,
};

function BrewerySearchMatchRow({
  group,
  selectedBreweryId,
  onSelectBrewery,
}) {
  const [overrideQuery, setOverrideQuery] = useState('');
  const [isOverriding, setIsOverriding] = useState(false);
  const debouncedOverride = useDebouncedValue(overrideQuery.trim(), 300);

  const searchQuery = isOverriding ? debouncedOverride : group.breweryName;
  const { breweries, isLoading, error } = useBreweriesSearch(
    group.stateCode,
    searchQuery,
    null,
    false,
  );

  const { options: globalOptions, loading: globalLoading } =
    useBreweryAutocompleteSearch(isOverriding ? debouncedOverride : '');

  const filteredGlobalOptions = useMemo(() => {
    if (!isOverriding || !group.stateCode) {
      return [];
    }
    return globalOptions.filter(
      (option) => option.state === group.stateCode,
    );
  }, [globalOptions, group.stateCode, isOverriding]);

  useEffect(() => {
    if (selectedBreweryId || isOverriding) {
      return;
    }
    if (isLoading || error || !breweries.length) {
      return;
    }
    const first = breweries[0];
    const breweryId = first?.id ?? first?.data?.id;
    if (breweryId) {
      onSelectBrewery(group.key, breweryId, formatBreweryLabel(first));
    }
  }, [
    breweries,
    error,
    group.key,
    isLoading,
    isOverriding,
    onSelectBrewery,
    selectedBreweryId,
  ]);

  const handleOverrideChange = useCallback((event, newValue) => {
    if (newValue?.id) {
      onSelectBrewery(group.key, newValue.id, newValue.name ?? '');
      setIsOverriding(false);
      setOverrideQuery('');
    }
  }, [group.key, onSelectBrewery]);

  const handleStartOverride = useCallback(() => {
    setIsOverriding(true);
    onSelectBrewery(group.key, '', '');
  }, [group.key, onSelectBrewery]);

  const handleOverrideInputChange = useCallback((event, value) => {
    setOverrideQuery(value);
  }, []);

  const matchLabel = selectedBreweryId
    ? (group.selectedLabel || selectedBreweryId)
    : isLoading
      ? 'Searching…'
      : 'No match found';

  return (
    <TableRow>
      <TableCell>{group.breweryName}</TableCell>
      <TableCell>{group.stateCode}</TableCell>
      <TableCell>
        {isLoading ? (
          <CircularProgress size={18} />
        ) : (
          <Typography variant="body2">{matchLabel}</Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" display="block">
            {error}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {isOverriding ? (
          <Autocomplete
            size="small"
            options={filteredGlobalOptions}
            getOptionLabel={formatBreweryOptionLabel}
            loading={globalLoading}
            onInputChange={handleOverrideInputChange}
            onChange={handleOverrideChange}
            renderInput={OverrideSearchField}
            sx={{ minWidth: 220 }}
          />
        ) : (
          <Typography
            component="button"
            type="button"
            variant="body2"
            onClick={handleStartOverride}
            sx={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
              p: 0,
            }}
          >
            Override match
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

BrewerySearchMatchRow.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string.isRequired,
    breweryName: PropTypes.string.isRequired,
    stateCode: PropTypes.string,
    selectedLabel: PropTypes.string,
  }).isRequired,
  selectedBreweryId: PropTypes.string,
  onSelectBrewery: PropTypes.func.isRequired,
};

/**
 * BreweryMatchingStep
 * Auto-matches sheet brewery names to Typesense breweries with override support.
 */
function BreweryMatchingStep({ groups, brewerySelections, onSelectBrewery }) {
  const unmatchedCount = groups.filter(
    (group) => !group.hasStateError && !brewerySelections[group.key]?.breweryId,
  ).length;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Review brewery matches. The first Typesense result is selected by default.
        Override any match before continuing.
      </Typography>
      {unmatchedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unmatchedCount} brewery group(s) still need a match before import.
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sheet brewery</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Matched brewery</TableCell>
              <TableCell>Override</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              if (group.hasStateError) {
                return <BreweryStateErrorRow key={group.key} group={group} />;
              }

              return (
                <BrewerySearchMatchRow
                  key={group.key}
                  group={{
                    ...group,
                    selectedLabel: brewerySelections[group.key]?.label,
                  }}
                  selectedBreweryId={brewerySelections[group.key]?.breweryId}
                  onSelectBrewery={onSelectBrewery}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

BreweryMatchingStep.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  brewerySelections: PropTypes.object.isRequired,
  onSelectBrewery: PropTypes.func.isRequired,
};

export default BreweryMatchingStep;
